/**
 * Service layer for I18n.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { UserLanguage } from '../services/repositories/api-repository';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly translate = inject(TranslateService);
  private readonly supportedLanguages: UserLanguage[] = ['en', 'fr'];
  private initPromise: Promise<void> | null = null;
  private initialized = false;
  private pendingLanguage: UserLanguage | null = null;

  async init(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.configureTranslations();

    return this.initPromise;
  }

  useLanguage(language: UserLanguage | string | null | undefined): void {
    const normalizedLanguage = this.normalizeLanguage(language);
    this.pendingLanguage = normalizedLanguage;

    if (!this.initialized) {
      void this.initPromise?.then(() => this.loadLanguage(normalizedLanguage));
      return;
    }

    void this.loadLanguage(normalizedLanguage);
  }

  currentLanguage(): UserLanguage {
    const activeLanguage = this.translate.currentLang || this.pendingLanguage;

    return activeLanguage === 'fr' ? 'fr' : 'en';
  }

  private async configureTranslations(): Promise<void> {
    this.translate.addLangs(this.supportedLanguages);
    this.translate.setDefaultLang('en');
    await firstValueFrom(this.translate.setFallbackLang('en'));
    const storedLanguage = localStorage.getItem('preferredLanguage')?.toLowerCase();
    const initialLanguage = this.pendingLanguage ?? (storedLanguage === 'fr' ? 'fr' : 'en');
    await this.loadLanguage(initialLanguage);
    this.initialized = true;
  }

  private async loadLanguage(language: UserLanguage): Promise<void> {
    await firstValueFrom(this.translate.use(language));
  }

  private normalizeLanguage(language: UserLanguage | string | null | undefined): UserLanguage {
    return String(language ?? '').toLowerCase() === 'fr' ? 'fr' : 'en';
  }
}
